// MatchList.js
import React, { useEffect, useMemo, useState } from 'react';
import MatchDay from './MatchDay';
import styles from './MatchList.module.css';

const TOP_LEAGUES = ['EKSTRAKLASA', 'PREMIER LEAGUE', 'LA LIGA', 'SERIE A', 'BUNDESLIGA', 'LIGUE 1', 'LIGA MISTRZÓW'];

const MatchList = () => {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // -- State for Filters --
  const [searchQuery, setSearchQuery] = useState('');
  const showReplays = false; // Always hide replays (no UI toggle for this)

  // -- Persisted State --
  const [selectedLeagueOptions, setSelectedLeagueOptions] = useState([]);

  const [favoriteTeams, setFavoriteTeams] = useState(() => {
    try {
      const saved = localStorage.getItem('favoriteTeams');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  // -- Effects --

  useEffect(() => {
    localStorage.setItem('favoriteTeams', JSON.stringify(favoriteTeams));
  }, [favoriteTeams]);

  useEffect(() => {
    const fetchMatches = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/flashscore-matches');
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setDays(data);

      } catch (err) {
        console.error('Fetch error:', err);
        setError('Nie udało się załadować danych.');
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, []);

  // -- Handlers --
  const toggleFavorite = (teamName) => {
    setFavoriteTeams(prev => {
      if (prev.includes(teamName)) return prev.filter(t => t !== teamName);
      return [...prev, teamName];
    });
  };

  const handleChipClick = (leagueName) => {
    // Toggle logic for chips: 
    // If clicked chip is already in selectedLeagues, remove it.
    // If not, ADD it (Multiselect behavior).
    const alreadySelected = selectedLeagueOptions.some(opt => opt.value === leagueName);
    let newSelection;
    if (alreadySelected) {
      newSelection = selectedLeagueOptions.filter(opt => opt.value !== leagueName);
    } else {
      newSelection = [...selectedLeagueOptions, { value: leagueName, label: leagueName }];
    }
    setSelectedLeagueOptions(newSelection);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedLeagueOptions([]);
    // Don't clear channels as they are "My Channels" settings
  };

  // -- Filtering Logic --
  const filteredDays = useMemo(() => {
    const selectedLeagues = selectedLeagueOptions.map(opt => opt.value);
    const searchLower = searchQuery.toLowerCase();

    return days.map(day => {
      let filteredMatches = day.matches.filter(match => {
        // 1. League Filter (via chips)
        const leagueMatch = !selectedLeagues.length || (match.league && selectedLeagues.some(sl => match.league.toUpperCase().includes(sl)));

        // 2. Replay Filter
        const replayMatch = showReplays || !match.isReplay;

        // 3. Text Search (teams, league, channels)
        const titleMatch = !searchQuery ||
          (match.home && match.home.toLowerCase().includes(searchLower)) ||
          (match.away && match.away.toLowerCase().includes(searchLower)) ||
          (match.title && match.title.toLowerCase().includes(searchLower)) ||
          (match.league && match.league.toLowerCase().includes(searchLower)) ||
          (match.channels || []).some(ch => ch.toLowerCase().includes(searchLower));

        return leagueMatch && replayMatch && titleMatch;
      });

      // -- Sorting Favorites to Top --
      if (filteredMatches.length > 0) {
        filteredMatches.sort((a, b) => {
          const aFav = favoriteTeams.includes(a.home) || favoriteTeams.includes(a.away) || favoriteTeams.includes(a.title);
          const bFav = favoriteTeams.includes(b.home) || favoriteTeams.includes(b.away) || favoriteTeams.includes(b.title);
          if (aFav && !bFav) return -1;
          if (!aFav && bFav) return 1;
          return 0; // Keep original time sort
        });
      }

      return { ...day, matches: filteredMatches };
    }).filter(day => day.matches.length > 0);
  }, [days, selectedLeagueOptions, showReplays, searchQuery, favoriteTeams]);

  if (loading) return <div className={styles.loading}>Ładowanie listy meczów (Flashscore)...</div>;
  if (error) return <div className={styles.error}>{error}</div>;

  return (
    <div className={styles.container}>
      {/* --- Search & Chips --- */}
      <div className={styles.filtersRow}>
        <input
          type="text"
          className={styles.searchBarInput}
          placeholder="Szukaj (drużyna, liga, kanał TV)..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <div className={styles.chipsContainer}>
          {TOP_LEAGUES.map(league => {
            const isActive = selectedLeagueOptions.some(opt => opt.value === league);
            return (
              <div
                key={league}
                className={`${styles.chip} ${isActive ? styles.active : ''}`}
                onClick={() => handleChipClick(league)}
              >
                {league}
              </div>
            );
          })}
        </div>
      </div>

      {(selectedLeagueOptions.length > 0 || searchQuery) && (
        <div style={{ marginBottom: '10px', textAlign: 'right' }}>
          <button onClick={clearFilters} className={styles.cleanFiltersBtn}>
            Wyczyść filtry
          </button>
        </div>
      )}

      {filteredDays.length === 0 ? (
        <p className={styles.noMatches}>Brak meczów spełniających wybrane kryteria.</p>
      ) : (
        filteredDays.map(day => (
          <MatchDay
            key={day.date}
            date={day.date}
            matches={day.matches}
            favoriteTeams={favoriteTeams}
            onToggleFavorite={toggleFavorite}
          />
        ))
      )}
    </div>
  );
};

export default MatchList;